import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import React, { useEffect } from 'react';

import { Close, Done } from '@mui/icons-material';
import { Typography } from '@mui/material';
import { green, red } from '@mui/material/colors';
import { AccessPointData } from '../../api/types';
import { getMissingPermissions } from '../../utils/access';
import { RFID_SCANNING_TIMEOUT } from '../../utils/utils';
import { AccessPointDetails } from '../AccessPointCard/AccessPointDetails';
import { Banner } from '../Banner/Banner';
import { PermissionContent } from '../Permissions/PermissionContent';
import { UserDetails } from '../UserDetails/UserDetails';

interface EmulateAccessPointResultProps {
    data: AccessPointData;
    onResetStep2: () => void;
    scannedUserPicture: string;
    setRfid: React.Dispatch<React.SetStateAction<string | null>>;
    translations: Record<string, string>;
}

export const EmulateAccessPointResult: React.FC<EmulateAccessPointResultProps> = ({
    data,
    onResetStep2,
    scannedUserPicture,
    setRfid,
    translations,
}) => {
    const { banner } = data;
    const missingPermissions = getMissingPermissions(data) || [];
    const permissionInfo = data.permissions?.find(
        ({ permissionCode }) => permissionCode === missingPermissions[0]?.permissionCode
    );

    useEffect(() => {
        const timeout = setTimeout(onResetStep2, RFID_SCANNING_TIMEOUT);
        return () => clearTimeout(timeout);
    }, [onResetStep2]);

    useEffect(() => {
        setRfid(null);
    }, [setRfid]);

    return (
        <>
            {banner && <Banner banner={banner} translations={translations} />}

            {data.accessSituation ? (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '1rem',
                        justifyContent: 'center',
                        background: green[800],
                        padding: '1rem 1rem 5rem',
                    }}
                >
                    <AccessPointDetails accessInformation={data} translations={translations} />

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Done sx={{ color: 'white' }} fontSize={'medium'} />
                        <Typography sx={{ color: 'white', fontWeight: 600 }} variant={'h6'}>
                            {translations.ACCESS_POINT_SITUATION_GRANTED}
                        </Typography>
                    </Box>
                </Box>
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '1rem',
                        justifyContent: 'center',
                        background: red[800],
                        padding: '1rem 1rem 5rem',
                    }}
                >
                    <AccessPointDetails accessInformation={data} translations={translations} />

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Close sx={{ color: 'white' }} fontSize={'medium'} />
                        <Typography sx={{ color: 'white', fontWeight: 600 }} variant={'h6'}>
                            {translations.ACCESS_POINT_SITUATION_DENIED}
                        </Typography>
                    </Box>
                </Box>
            )}

            <Box mt={'-4rem'}>
                <UserDetails firstName={data.firstName} lastName={data.lastName} userPicture={scannedUserPicture} />
            </Box>

            {!data.accessSituation && missingPermissions && data.permissions && permissionInfo && (
                <PermissionContent
                    accessInformation={data}
                    permissionInfo={permissionInfo}
                    readOnly
                    translations={translations}
                />
            )}

            <Button
                fullWidth
                color="primary"
                variant="contained"
                type="button"
                size="large"
                onClick={onResetStep2}
                style={{ display: 'none' }}
            >
                {translations.EMULATE_ACCESS_POINT_RESCAN_NFC_BUTTON}
            </Button>
        </>
    );
};
